import { type StaticColumn } from 'src/app/services/labpartner.service.model';

const DEFAULT_MAX_CONCENTRATION = 1000000000;

export class AnalyteColumns {
  static lodAnalyteColumns: StaticColumn[] = [
    {
      prop: 'analyteTemplateId',
      header: 'Analyte Name',
      required: true,
      isCustom: true,
      inputType: 'select',
      options: [],
      valueType: 'number',
    },
    {
      prop: 'strainIsolateSubtype',
      header: 'Analyte Strain/Isolate/Subtype',
      required: true,
    },
    {
      prop: 'source',
      header: 'Source',
      required: true,
    },
    {
      prop: 'partNumber',
      header: 'Analyte Part Number',
      required: true,
    },
    {
      prop: 'lotNumber',
      header: 'Analyte Lot Number',
      required: true,
    },
    {
      prop: 'concentration1x',
      header: 'Analyte Concentration Value',
      isCustom: true,
      inputType: 'number',
      valueType: 'scientific',
      required: true,
      minValue: 0,
      maxValue: DEFAULT_MAX_CONCENTRATION,
    },
    {
      prop: 'concentration1xUnit',
      header: 'Analyte Concentration Unit',
      required: true,
    },
  ];

  static precisionAnalyteColumns: StaticColumn[] = [
    {
      prop: 'analyteTemplateId',
      header: 'Analyte Name',
      required: true,
      isCustom: true,
      inputType: 'select',
      options: [],
      valueType: 'number',
    },
    {
      prop: 'strainIsolateSubtype',
      header: 'Analyte Strain/Isolate/Subtype',
      required: true,
    },
    {
      prop: 'precisionSource',
      header: 'Analyte Source',
      required: true,
    },
    {
      prop: 'precisionPartNumber',
      header: 'Analyte Part Number',
      required: true,
    },
    {
      prop: 'precisionLotNumber',
      header: 'Analyte Lot Number',
      required: true,
    },
    {
      prop: 'concentration1x',
      header: 'Analyte Concentration Value',
      isCustom: true,
      inputType: 'number',
      valueType: 'scientific',
      required: true,
      minValue: 0,
      maxValue: DEFAULT_MAX_CONCENTRATION,
    },
    {
      prop: 'concentration1xUnit',
      header: 'Analyte Concentration Unit',
      required: true,
    },
    {
      prop: 'relativeLOD',
      header: 'Analyte Relative LOD',
      required: true,
    },
  ];

  static generalAnalyteColumns: StaticColumn[] = [
    {
      prop: 'analyteTemplateId',
      header: 'Analyte Name',
      required: true,
      isCustom: true,
      inputType: 'select',
      options: [],
      valueType: 'number',
    },
    {
      prop: 'strainIsolateSubtype',
      header: 'Analyte Strain/Isolate/Subtype',
      required: false,
    },
    {
      prop: 'concentration1x',
      header: 'Analyte Concentration Value',
      isCustom: true,
      inputType: 'number',
      valueType: 'scientific',
      required: true,
      minValue: 0,
      maxValue: DEFAULT_MAX_CONCENTRATION,
    },
    {
      prop: 'concentration1xUnit',
      header: 'Analyte Concentration Unit',
      required: true,
    },
  ];

  static intSubsAnalyteColumns: StaticColumn[] = [
    {
      prop: 'analyteTemplateId',
      header: 'Analyte Name',
      required: true,
      isCustom: true,
      inputType: 'select',
      options: [{ value: 0, label: 'None' }],
      valueType: 'number',
    },
    {
      prop: 'strainIsolateSubtype',
      header: 'Analyte Strain/Isolate/Subtype',
      required: true,
    },
    {
      prop: 'intSubsSource',
      header: 'Analyte Source',
      required: true,
    },
    {
      prop: 'intSubsPartNumber',
      header: 'Analyte Part Number',
      required: true,
    },
    {
      prop: 'intSubsLotNumber',
      header: 'Analyte Lot Number',
      required: true,
    },
    {
      prop: 'concentration1x',
      header: 'Analyte Concentration Value',
      isCustom: true,
      inputType: 'number',
      valueType: 'scientific',
      required: true,
      minValue: 0,
      maxValue: DEFAULT_MAX_CONCENTRATION,
    },
    {
      prop: 'concentration1xUnit',
      header: 'Analyte Concentration Unit',
      required: true,
    },
    {
      prop: 'intSubsRelativeLOD',
      header: 'Analyte Relative LOD',
      required: true,
      inputType: 'number',
      valueType: 'number',
      minValue: 0,
    },
  ];

  static crossReactivityAndMicrobialInterferenceAnalyteColumns: StaticColumn[] = [
    {
      prop: 'analyteTemplateId',
      header: 'Analyte Name',
      required: true,
      isCustom: true,
      inputType: 'select',
      options: [{ value: 0, label: 'None' }],
      valueType: 'number',
    },
    {
      prop: 'strainIsolateSubtype',
      header: 'Analyte Subtype/Strain/Isolate',
      required: true,
    },
    {
      prop: 'crmiAnalyteSource',
      header: 'Analyte Source',
      required: true,
    },
    {
      prop: 'crmiAnalytePartNumber',
      header: 'Analyte Part Number',
      required: true,
    },
    {
      prop: 'crmiAnalyteLotNumber',
      header: 'Analyte Lot Number',
      required: true,
    },
    {
      prop: 'concentration1xUnit',
      header: 'Analyte Concentration Unit',
      required: true,
    },
    {
      prop: 'concentration1x',
      header: 'Analyte Concentration Value',
      isCustom: true,
      inputType: 'number',
      valueType: 'scientific',
      required: true,
      minValue: 0,
      maxValue: DEFAULT_MAX_CONCENTRATION,
    },
    {
      prop: 'crmiAnalyteRelativeLOD',
      header: 'Analyte Relative LOD',
      required: true,
      inputType: 'number',
      valueType: 'number',
      minValue: 0,
    },
  ];

  static transportMediaAndSpecimenStabilityAnalyteColumns: StaticColumn[] = [
    {
      prop: 'analyteTemplateId',
      header: 'Analyte Name',
      required: true,
      isCustom: true,
      inputType: 'select',
      options: [{ value: 0, label: 'None' }],
      valueType: 'number',
    },
    {
      prop: 'strainIsolateSubtype',
      header: 'Analyte Subtype/Strain/Isolate',
      required: true,
    },
    {
      prop: 'tmssAnalyteSource',
      header: 'Analyte Source',
      required: true,
    },
    {
      prop: 'tmssAnalytePartNumber',
      header: 'Analyte Part Number',
      required: true,
    },
    {
      prop: 'tmssAnalyteLotNumber',
      header: 'Analyte Lot Number',
      required: true,
    },
    {
      prop: 'concentration1x',
      header: 'Analyte Concentration Value',
      isCustom: true,
      inputType: 'number',
      valueType: 'scientific',
      required: true,
      minValue: 0,
      maxValue: DEFAULT_MAX_CONCENTRATION,
    },
    {
      prop: 'concentration1xUnit',
      header: 'Analyte Concentration Unit',
      required: true,
    },
    {
      prop: 'tmssAnalyteRelativeLOD',
      header: 'Analyte Relative LOD',
      required: true,
      inputType: 'number',
      valueType: 'number',
      minValue: 0,
    },
  ];
}
